import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/layout/PageHero';

const CONTACT_EMAIL = 'contact@autoxium.com';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen" data-testid="terms-of-service-page">
      <PageHero
        eyebrow="Legal"
        title="Terms of Service"
        description="The rules that govern your use of the AUTOXIUM platform."
      />
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl prose-autoxium">
            <p className="text-sm text-muted-foreground">Last updated: September 26, 2026</p>

            <h2>About These Terms</h2>
            <p>
              AUTOXIUM (autoxium.com) is operated by an individual based in India, referred to
              here as <strong>the owner of autoxium.com</strong> ("we", "us"). These Terms are an
              agreement between you and the owner. By creating an account or otherwise using
              AUTOXIUM, you agree to these Terms, our{' '}
              <Link to="/privacy-policy">Privacy Policy</Link> and our{' '}
              <Link to="/content-guidelines">Content Guidelines</Link>. If you do not agree,
              please do not use the platform.
            </p>

            <h2>Eligibility</h2>
            <p>
              You must be at least 18 years old to create an account. Articles, topics and
              tools can be read without an account.
            </p>

            <h2>Your Account</h2>
            <p>
              You must provide accurate registration information and keep your password
              secure. You're responsible for all activity under your account; tell us promptly
              at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you suspect
              unauthorised use.
            </p>

            <h2>Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>post content that is unlawful, defamatory, obscene, harassing, hateful, or that infringes anyone's intellectual property or privacy;</li>
              <li>post content that is prohibited under the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021;</li>
              <li>misrepresent technical information in a way that could create safety risks, or impersonate another person;</li>
              <li>post spam, advertising, or malicious code;</li>
              <li>attempt to disrupt, reverse engineer, or gain unauthorised access to the platform;</li>
              <li>use automated means to scrape or bulk-download content without permission.</li>
            </ul>

            <h2>Content Ownership</h2>
            <h3>Your Content</h3>
            <p>
              You keep ownership of the articles, questions, answers and other content you
              submit, and you confirm you have the right to post it. By submitting content, you
              grant the owner of autoxium.com a non-exclusive, worldwide, royalty-free licence
              to host, store, display, reproduce, adapt for formatting, and distribute that
              content on and in connection with AUTOXIUM (including in PDF downloads and
              promotion of the platform). This licence ends when you or we delete the content,
              except for copies in routine backups for a limited time, and answers or quotes
              that other users have built on, which may remain in anonymised form.
            </p>
            <h3>Site Content</h3>
            <p>
              The AUTOXIUM name, logo, design, and platform-authored articles, diagrams, tools
              and simulators are owned by the owner of autoxium.com and may not be reproduced,
              republished, or used commercially without written permission. You may share links
              and short quotations with attribution.
            </p>

            <h2>Moderation and Removal</h2>
            <p>
              We may review, edit for formatting, refuse, or remove any content, and suspend or
              terminate any account, that breaches these Terms or the Content Guidelines, or
              when required by law. You can report content using the Report button on
              community posts.
            </p>

            <h2>Copyright and Content Complaints</h2>
            <p>
              If you believe content on AUTOXIUM infringes your copyright or other rights, or is
              otherwise unlawful, email the Grievance Officer below with: the URL of the
              content, a description of the problem, proof of your rights (for copyright), and
              your contact details. We acknowledge complaints within 24 hours and act on them
              within 15 days, as required by the IT Rules, 2021. Accounts that repeatedly
              infringe will be terminated.
            </p>

            <h2>Tools, Calculators and Technical Content — Disclaimer</h2>
            <p>
              Articles, tools, calculators, simulators, and community answers are provided for
              <strong> educational and informational purposes only</strong>, "as is" and "as
              available". Tool results use simplified models and are indicative only. AUTOXIUM
              does not guarantee the accuracy, completeness, or suitability of any content, and
              nothing on the platform is professional engineering advice. Always follow
              manufacturer documentation and applicable standards, and have any safety-critical
              or high-voltage design checked by a qualified engineer before acting on it.
            </p>

            <h2>Third-Party Links</h2>
            <p>
              AUTOXIUM may link to external websites or documents. We don't control them and
              are not responsible for their content or privacy practices.
            </p>

            <h2>Limitation of Liability</h2>
            <p>
              AUTOXIUM is a free platform. To the fullest extent permitted by law, the owner of
              autoxium.com and contributors are not liable for any indirect, incidental,
              special, or consequential loss or damage, or for any loss arising from reliance on
              content or tool results. Where liability cannot be excluded, it is limited to
              INR 1,000. Nothing in these Terms limits liability that cannot be limited under
              Indian law.
            </p>

            <h2>Indemnity</h2>
            <p>
              You agree to indemnify the owner of autoxium.com against claims, losses, and
              reasonable legal costs arising from content you post or your breach of these
              Terms.
            </p>

            <h2>Ending Your Use</h2>
            <p>
              You can stop using AUTOXIUM at any time and ask us to delete your account by
              emailing <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We may suspend
              or end your access if you breach these Terms. Sections on content licences (for
              content that remains), disclaimers, limitation of liability, indemnity, and
              governing law continue to apply afterwards.
            </p>

            <h2>Governing Law and Disputes</h2>
            <p>
              These Terms are governed by the laws of India. We'll try to resolve any dispute
              informally first — please contact us. Any dispute that can't be resolved this way
              is subject to the exclusive jurisdiction of the competent courts in India.
            </p>

            <h2>Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. For material changes, we will update
              the date above and notify registered users by email at least 15 days before the
              changes take effect. Continuing to use AUTOXIUM after that date means you accept
              the revised Terms.
            </p>

            <h2>General</h2>
            <p>
              If any part of these Terms is found unenforceable, the rest remains in effect.
              These Terms, together with the Privacy Policy and Content Guidelines, are the
              entire agreement between you and us about AUTOXIUM. Our not enforcing a term is
              not a waiver of it.
            </p>

            <h2>Grievance Officer and Contact</h2>
            <p>
              <strong>Grievance Officer:</strong> The owner of autoxium.com<br />
              <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> (subject line "Grievance")<br />
              <strong>Location:</strong> India
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
